import { useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Navbar } from "../Navbar";
import { Box, Grid } from "@mui/material";
import { useEffect, useRef } from "react";
import { useAppSelector } from "../../store/store";
import { selectAuth } from "../../store/auth";
import { PageHeader, PageHeaderActions } from "./PageHeader";
import "./SecuredContainer.scss";
import { PageFooterActions } from "./PageFooter";
import { useInitUser } from "../../hooks/auth";
import { useToggle } from "../../hooks/utilities";
import { datadogRum } from "@datadog/browser-rum";
import { selectDefaultEntity } from "../../store/entity";
import Cookies from "js-cookie";
import { refresh } from "../../api/auth";

/**
 * The secured container ensures that a user is logged in.
 * If no user is authenticated, it redirects to the login page.
 * @constructor
 */
export const SecuredContainer = ({ children }) => {
  const navigate = useNavigate();
  const authState = useAppSelector(selectAuth);
  const entity = useAppSelector(selectDefaultEntity);
  const initUser = useInitUser();
  const [sidebarOpen, toggleSidebar] = useToggle(false, "sidebar");
  const hasRefreshedRef = useRef(false);

  // Refresh token once on app startup (hard refresh)
  useEffect(() => {
    if (hasRefreshedRef.current) return;
    
    const token = Cookies.get("ptkr-token") || localStorage.getItem("ptkr-token");
    if (token) {
      hasRefreshedRef.current = true;
      refresh().catch((err) => {
        console.error("Failed to refresh token:", err);
        // If refresh fails, continue with existing token
      });
    }
  }, []);

  // Handle authentication check and redirect
  useEffect(() => {
    if (!authState || !authState.authenticated) {
      const cookie = Cookies.get("ptkr-token");
      if (!cookie) {
        navigate("/auth/login");
      } else {
        initUser().then();
      }
    }
  }, [authState, initUser, navigate]);

  // Set datadog user information
  useEffect(() => {
    if (authState?.authenticated && entity) {
      datadogRum.setUser({
        id: authState.userKey,
        name: `${authState.firstName} ${authState.lastName}`,
        email: authState.email,
        entity: entity.name,
        entityKey: entity._id,
        evaluation: entity.expiresOn != null,
      });
    }
  }, [authState, entity]);

  const offsetEm = 4;

  // Don't render content if user is not authenticated
  if (!authState || !authState.authenticated) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <div>Loading...</div>
      </Box>
    );
  }

  return (
    <>
      <Grid className="full-height" container>
        <Grid item>
          <Navbar onOpenSidebar={toggleSidebar} />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex" }} className="full-height">
            <Sidebar open={sidebarOpen} />
            <Box
              // sx={{ marginLeft: "240px", marginTop: "4.8em", height: "100%" }}
              sx={{ flexGrow: 1, paddingTop: `${offsetEm}em` }}
              height={`calc(100% - ${offsetEm}em)`}
            >
              <div className="bg-white h-[50px] border-b border-gray-200 flex items-center space-x-4">
                <PageHeader />
                <PageHeaderActions />
              </div>
              {children}
              <PageFooterActions />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
